import { orderBy } from 'lodash-es'
import { type ReactNode, useState } from 'react'

import { FvNum, QuoteCarrier } from '@fv/client-components'
import { type DTO, formatMoney, quoteUtils } from '@fv/models/core'

import { AdminSliderPanel } from '@/components/AdminSliderPanel'
import { AdminBadge } from '@/components/shared/AdminBadge'
import { AdminButton } from '@/components/shared/AdminButton'
import { AdminDate } from '@/components/shared/AdminDate'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminTable, type SortDef } from '@/components/shared/AdminTable'
import type { AdminLoad, AdminQuote } from '@/types/admin-load'

import { useForceRerate } from '../../quotes/mutations'
import { QuoteDetailsPanel } from './QuoteDetailsPanel'
import { QuoteHistoryTable } from './QuoteHistoryTable'
import { SerializeSlider } from './SerializeSlider'
import type { LoadPanelProps } from './types'

export function QuotesPanel({ load }: LoadPanelProps) {
  const [sort, setSort] = useState<SortDef>({
    key: 'amount',
    dir: 'asc',
  })
  const [selectedQuoteHistory, setSelectedQuoteHistory] =
    useState<DTO<AdminQuote>>(null)
  const [selectedQuoteDetails, setSelectedQuoteDetails] =
    useState<DTO<AdminQuote>>(null)

  // make sure error'd quotes are always at the bottom unless sorted by status
  const sortByKeys = sort.key === 'status' ? ['status'] : ['status', sort.key]
  const sortDirections: Array<'asc' | 'desc'> =
    sort.key === 'status' ? [sort.dir] : ['asc', sort.dir]
  const sortedQuotes = orderBy(load.quotes, sortByKeys, sortDirections)

  return (
    <AdminSection title="Quotes" headerActions={<QuoteActions load={load} />}>
      <AdminTable
        data={sortedQuotes}
        rowKey={q => q._id}
        defaultSort={sort.key}
        defaultSortDir={sort.dir}
        onSortChange={setSort}
        onRowSelect={setSelectedQuoteDetails}
        columns={[
          {
            key: 'createdDate',
            label: 'Created Date',
            render: q => <AdminDate val={q.createdDate} />,
          },
          {
            key: 'assetCarrierName',
            label: 'Carrier',
            render: q => <QuoteCarrier quote={q} />,
          },
          { key: 'quoteNum', label: 'Quote Number', render: d => d.quoteNum },
          { key: 'status', label: 'Status', render: d => d.status },
          { key: 'source', label: 'Source', render: d => d.source },
          {
            key: 'method',
            label: 'Method',
            render: d => `${d.method} ${d.scrape ? '(scrape)' : ''}`,
          },
          {
            key: 'amount',
            label: 'Total',
            render: d => <>{formatMoney(d.amount, d.currency)}</>,
          },
          ...(load.createdBy?.markup
            ? [
                {
                  key: 'vendorAmount' as const,
                  label: 'Markup Total',
                  render: (d: DTO<AdminQuote>) =>
                    quoteUtils.calculateMarkup(
                      d,
                      load.createdBy.markup,
                      load.exchangeRates,
                    ).amount,
                },
              ]
            : []),
          {
            key: 'filterName',
            label: 'Filtered',
            render: d =>
              d.status === 'filtered' ? (
                <AdminBadge className="badge-sm badge-warning">
                  {d.filterName || 'Yes'}
                </AdminBadge>
              ) : null,
          },
          {
            key: 'history',
            label: '',
            render: q => {
              return (q.quoteHistory?.length ?? 0) > 0 ? (
                <AdminButton
                  tooltip="View history"
                  onClick={() => setSelectedQuoteHistory(q)}
                  icon="history"
                  className="btn-xs btn-outline"
                />
              ) : null
            },
          },
        ]}
      />

      <AdminSliderPanel
        title={`Quote History - ${selectedQuoteHistory?.quoteNum || 'Quote'}`}
        isOpen={!!selectedQuoteHistory}
        onClose={() => setSelectedQuoteHistory(null)}
      >
        {selectedQuoteHistory && (
          <QuoteHistoryTable history={selectedQuoteHistory.quoteHistory} />
        )}
      </AdminSliderPanel>

      <AdminSliderPanel
        title={`Quote Details - ${selectedQuoteDetails?.assetCarrierName || 'Quote'}`}
        isOpen={!!selectedQuoteDetails}
        onClose={() => setSelectedQuoteDetails(null)}
        width="50vw"
      >
        {selectedQuoteDetails && (
          <QuoteDetailsPanel
            quote={selectedQuoteDetails}
            markup={load.createdBy?.markup}
            exchangeRates={load.exchangeRates}
          />
        )}
      </AdminSliderPanel>
    </AdminSection>
  )
}

const QuoteActions = ({ load }: { load: AdminLoad }) => {
  const forceRerate = useForceRerate(load._id)
  const canForceRerate = load.status === 'quoting'
  const markup = getMarkupCalcString(load)
  return (
    <>
      {canForceRerate && (
        <AdminButton
          onClick={() => forceRerate.mutate()}
          icon="redo"
          className="btn-sm"
          loading={forceRerate.isPending}
        >
          Force Rerate
        </AdminButton>
      )}

      {markup && (
        <AdminBadge className="badge-sm badge-primary">{markup}</AdminBadge>
      )}

      <SerializeSlider loadId={load._id} output="rating" />
    </>
  )
}

const getMarkupCalcString = (load: AdminLoad): ReactNode => {
  if (load.status !== 'booked') {
    return null
  }

  const calc = quoteUtils.calculateMarkup(
    load.selectedQuote,
    load.createdBy.markup,
    load.exchangeRates,
  )

  if (calc.method === 'none' || !calc.charge) return null

  const showPercentExp =
    calc.method === 'percent' ||
    calc.method === 'minimum' ||
    calc.method === 'maximum'
  return (
    <>
      Markup of&nbsp;
      <FvNum currency={calc.charge.currency} val={calc.charge.amount} />
      {showPercentExp && (
        <>
          &nbsp;(based on {load.createdBy.markup.percent}% of $
          <FvNum val={load.selectedQuote.amount} />
          {calc.method === 'minimum' && (
            <>
              &nbsp;not meeting the minimum of $
              <FvNum val={load.createdBy.markup.minimum} />
            </>
          )}
          {calc.method === 'maximum' && (
            <>
              &nbsp;exceeding the maximum of $
              <FvNum val={load.createdBy.markup.maximum} />
            </>
          )}
          )
        </>
      )}
      &nbsp;has been applied.
    </>
  )
}
